import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import NotesInput from "../../../components/dr/NotesInput";
import Alert from "../../../components/v3/Alert";
import Btn from "../../../components/v3/Btn";
import Field from "../../../components/v3/Field";
import { drApi } from "../../../services/drApi";
import { apiErrorMessage } from "../../../utils/apiError";

const MIN_REASON = 10;

/**
 * OversightActions - what a Platform Manager can do with an approval.
 *
 * AD no longer decides KYC, classification or suitability. What it still owns
 * is the flow of work: hand an item to a named reviewer, or escalate it. Both
 * carry a mandatory reason because both change who is accountable for a
 * regulated decision, and that has to be answerable afterwards.
 *
 * The reviewer picker reads GET admin/compliance/reviewers. That endpoint may
 * be absent or refused, which must not strand the AD: the numeric-id field
 * below the picker is always live, so a reassignment is possible with nothing
 * but the reviewer's user id.
 */
function OversightActions({ slaId, onDone }) {
  const [mode, setMode] = useState(null); // "reassign" | "escalate"
  const [reviewers, setReviewers] = useState([]);
  const [reviewersError, setReviewersError] = useState(null);
  const [reviewerId, setReviewerId] = useState("");
  const [reviewerIdFallback, setReviewerIdFallback] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // onDone refetches the whole detail screen, so this component can be gone by
  // the time the submit chain resumes. Without the guard React warns about a
  // state update on an unmounted component every time an AD navigates away
  // during a reassignment.
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  useEffect(() => {
    let live = true;
    drApi
      .reviewers()
      .then((res) => {
        if (!live) return;
        const list = Array.isArray(res) ? res : res?.data;
        setReviewers(Array.isArray(list) ? list : []);
        setReviewersError(null);
      })
      .catch((err) => {
        if (!live) return;
        setReviewers([]);
        setReviewersError(apiErrorMessage(err, "Reviewer list unavailable"));
      });
    return () => {
      live = false;
    };
  }, []);

  function open(next) {
    setMode(next);
    setReason("");
    setReviewerId("");
    setReviewerIdFallback("");
    setError(null);
  }

  function close() {
    if (busy) return;
    setMode(null);
  }

  const trimmedReason = reason.trim();
  const reasonValid = trimmedReason.length >= MIN_REASON;
  const chosenReviewer = Number(reviewerId || reviewerIdFallback) || null;
  const canSubmit =
    reasonValid && !busy && (mode === "escalate" || !!chosenReviewer);

  async function submit() {
    setBusy(true);
    setError(null);
    let done = null;
    try {
      if (mode === "reassign") {
        await drApi.adReassign(slaId, {
          reviewer_user_id: chosenReviewer,
          reason: trimmedReason,
        });
        if (!alive.current) return;
        done = "Reassigned. The new reviewer now owns this item.";
      } else {
        await drApi.adEscalate(slaId, { reason: trimmedReason });
        if (!alive.current) return;
        done = "Escalated for compliance attention.";
      }
      setMode(null);
      setBusy(false);
      // The confirmation belongs to the PAGE, not to this component: onDone
      // refetches the detail, which unmounts everything below the loader, and
      // a message held here would vanish with it.
      if (onDone) await onDone(done);
    } catch (err) {
      if (!alive.current) return;
      setError(apiErrorMessage(err, "Action failed"));
      setBusy(false);
    }
  }

  const reviewerLabel = (r) =>
    `${r.name || r.email || `Reviewer #${r.id}`}${
      Array.isArray(r.roles) && r.roles.length ? ` (${r.roles.join(", ")})` : ""
    }`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Alert tone="info">
        Platform Manager sessions can reassign or escalate but do not decide
        KYC, classification or suitability.
      </Alert>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Btn variant="primary" onClick={() => open("reassign")}>Reassign</Btn>
        <Btn onClick={() => open("escalate")}>Escalate</Btn>
      </div>

      <Dialog
        open={mode !== null}
        onClose={close}
        aria-labelledby="oversight-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="oversight-dialog-title">
          {mode === "escalate" ? "Escalate this approval" : "Reassign this approval"}
        </DialogTitle>
        <DialogContent>
          {mode === "reassign" && (
            <>
              <Field
                as="select"
                label="Reviewer"
                name="oversight-reviewer"
                value={reviewerId}
                onChange={(e) => setReviewerId(e.target.value)}
                disabled={busy}
              >
                <option value="">Select a reviewer</option>
                {reviewers.map((r) => (
                  <option key={r.id} value={r.id}>{reviewerLabel(r)}</option>
                ))}
              </Field>
              {reviewersError && (
                <Alert tone="warn">
                  Reviewer list unavailable. Enter the reviewer's user id below.
                </Alert>
              )}
              <Field
                label="Or reviewer user id"
                name="oversight-reviewer-id"
                type="number"
                value={reviewerIdFallback}
                onChange={(e) => setReviewerIdFallback(e.target.value)}
                disabled={busy}
              />
            </>
          )}

          <NotesInput
            id="oversight-reason"
            label={`Reason (mandatory, min ${MIN_REASON} chars)`}
            value={reason}
            onChange={setReason}
            minLength={MIN_REASON}
            required
            disabled={busy}
          />

          {error && <Alert tone="danger">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Btn onClick={close} disabled={busy}>Cancel</Btn>
          <Btn variant="primary" onClick={submit} disabled={!canSubmit}>
            {busy ? "Saving..." : mode === "escalate" ? "Escalate" : "Reassign"}
          </Btn>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default OversightActions;
